import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button as BootstrapButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BiMinus } from 'react-icons/bi';
import { BsPlus } from 'react-icons/bs';
import { AiOutlineDelete } from 'react-icons/ai';
import preview from 'assets/img/preview.jpg';
import { formatCurrency } from 'utils/currencyFormatter';
import ModalCustom from 'components/backoffice/modals/ModalCustom';
import SearchProduct from 'components/backoffice/customInput/SearchProduct';
import Button from 'components/backoffice/buttonCustom';
import './AddItemsToOrderModal.scss';

const AddItemsToOrderModal = ({ show, onHide, order, articles = [], loading = false }) => {
  const { t } = useTranslation(['backoffice']);
  const [productQuantities, setProductQuantities] = useState({});
  const [newProducts, setNewProducts] = useState([]);

  useEffect(() => {
    if (!show) {
      setProductQuantities({});
      setNewProducts([]);
    }
  }, [show]);

  useEffect(() => {
    if (show && articles && articles.length > 0) {
      const initialQuantities = {};
      articles.forEach((article) => {
        initialQuantities[article.id] = article.quantity || 0;
      });
      setProductQuantities(initialQuantities);
    }
  }, [articles, show]);

  const handleQuantityChange = (articleId, delta) => {
    setProductQuantities((prev) => {
      const currentQuantity = prev[articleId] || 0;
      const newQuantity = Math.max(0, currentQuantity + delta);
      return { ...prev, [articleId]: newQuantity };
    });
  };

  const handleRemoveExistingProduct = (articleId) => {
    setProductQuantities((prev) => {
      return { ...prev, [articleId]: 0 };
    });
  };

  const handleNewProductSelect = (product) => {
    if (product && !newProducts.find((p) => p.id === product.id)) {
      setNewProducts((prev) => [...prev, { ...product, quantity: 1 }]);
    }
  };

  const handleNewProductQuantityChange = (productId, delta) => {
    setNewProducts((prev) =>
      prev.map((product) => {
        if (product.id === productId) {
          const newQuantity = Math.max(1, (product.quantity || 1) + delta);
          return { ...product, quantity: newQuantity };
        }
        return product;
      })
    );
  };

  const handleRemoveNewProduct = (productId) => {
    setNewProducts((prev) => prev.filter((product) => product.id !== productId));
  };

  const totalToAdd = useMemo(() => {
    let total = 0;

    articles.forEach((article) => {
      const currentQuantity = productQuantities[article.id] || 0;
      const originalQuantity = article.quantity || 0;
      const quantityChange = currentQuantity - originalQuantity;
      if (quantityChange > 0) {
        const precio = typeof article.precio_venta === 'string' ? parseFloat(article.precio_venta) || 0 : (article.precio_venta || 0);
        total += quantityChange * precio;
      }
    });

    newProducts.forEach((product) => {
      total += (product.quantity || 1) * (product.price || 0);
    });

    return total;
  }, [articles, productQuantities, newProducts]);

  const handleConfirm = () => {
    const changes = {
      existingProducts: [],
      newProducts: [],
    };

    articles.forEach((article) => {
      const currentQuantity = productQuantities[article.id] ?? (article.quantity || 0);
      changes.existingProducts.push({
        id: article.id,
        quantity: currentQuantity,
        article: article, 
      });
    });

    changes.newProducts = newProducts.map((product) => ({
      id: product.id,
      quantity: product.quantity || 1,
      product: product, 
    }));

    onHide(changes);
  };

  const handleCancel = () => {
    setProductQuantities({});
    setNewProducts([]);
    onHide();
  };

  const footer = (
    <div className="d-flex justify-content-between w-100 align-items-center">
      <div className="total-to-add">
        <strong>{t('addItemsModal.totalToAdd')}: {formatCurrency(totalToAdd)}</strong>
      </div>
      <div className="d-flex gap-2">
        <Button color="secondary" onClick={handleCancel} disabled={loading} extraClass="btn-cancel-custom">
          {t('cancel')}
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={loading} extraClass="btn-confirm-custom">
          {loading ? t('save') : t('confirm')}
        </Button>
      </div>
    </div>
  );

  return (
    <ModalCustom
      show={show}
      onHide={handleCancel}
      title={t('addItemsModal.title', { code: order?.code || '' })}
      size="lg"
      centered
      footer={footer}
      classNameBody="add-items-modal-body"
      className="add-items-modal"
    >
      <Row className="g-3">
        <Col md={12}>
          <h5 className="mb-3">{t('addItemsModal.orderProducts')}</h5>
          {articles && articles.length > 0 ? (
            <div className="order-products-list">
              {articles
                .filter((article) => {
                  const currentQuantity = productQuantities[article.id] ?? (article.quantity || 0);
                  return currentQuantity > 0;
                })
                .map((article) => {
                  const currentQuantity = productQuantities[article.id] ?? (article.quantity || 0);
                  return (
                    <div key={article.id} className="product-item mb-3 p-3 border rounded">
                    <Row className="align-items-center">
                      <Col md={2}>
                        <img
                          src={article.product?.cover || preview}
                          alt={article.product?.title}
                          className="product-image"
                        />
                      </Col>
                      <Col md={6}>
                        <div className="product-info">
                          <h6 className="mb-1">{article.product?.title}</h6>
                          <p className="mb-0 text-muted small">
                            {article.product?.editorial} • {formatCurrency(article.precio_venta)}
                          </p>
                        </div>
                      </Col>
                      <Col md={4} className="d-flex align-items-center justify-content-end gap-2">
                        <div className="quantity-selector d-flex align-items-center gap-2">
                          <BootstrapButton
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleQuantityChange(article.id, -1)}
                            disabled={currentQuantity === 0}
                          >
                            <BiMinus />
                          </BootstrapButton>
                          <span className="quantity-display">{currentQuantity}</span>
                          <BootstrapButton
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleQuantityChange(article.id, 1)}
                          >
                            <BsPlus />
                          </BootstrapButton>
                        </div>
                        <BootstrapButton
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveExistingProduct(article.id)}
                          className="delete-button"
                          title={t('remove')}
                        >
                          <AiOutlineDelete />
                        </BootstrapButton>
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </div>
          ) : articles && articles.filter((article) => {
              const currentQuantity = productQuantities[article.id] ?? (article.quantity || 0);
              return currentQuantity > 0;
            }).length === 0 ? (
            <p className="text-muted">{t('addItemsModal.noProducts')}</p>
          ) : null}
        </Col>

        <Col md={12} className="mt-4">
          <h5 className="mb-3">{t('addItemsModal.searchNewProducts')}</h5>
          <div className="search-products-section mb-3">
            <SearchProduct
              filterBy="titulo"
              fields="id,title,image,ean,editorial,price"
              placeholder={t('addItemsModal.searchPlaceholder')}
              onSelected={handleNewProductSelect}
            />
          </div>

          {newProducts.length > 0 && (
            <div className="new-products-list">
              {newProducts.map((product) => (
                <div key={product.id} className="product-item mb-3 p-3 border rounded">
                  <Row className="align-items-center">
                    <Col md={2}>
                      <img
                        src={product.image || preview}
                        alt={product.title}
                        className="product-image"
                      />
                    </Col>
                    <Col md={6}>
                      <div className="product-info">
                        <h6 className="mb-1">{product.title}</h6>
                        <p className="mb-0 text-muted small">
                          {typeof product.editorial === 'object' 
                            ? product.editorial?.nombre_fiscal || product.editorial?.label 
                            : product.editorial} • {formatCurrency(product.price)}
                        </p>
                      </div>
                    </Col>
                    <Col md={4} className="d-flex align-items-center justify-content-end gap-2">
                      <div className="quantity-selector d-flex align-items-center gap-2">
                        <BootstrapButton
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleNewProductQuantityChange(product.id, -1)}
                          disabled={product.quantity === 1}
                        >
                          <BiMinus />
                        </BootstrapButton>
                        <span className="quantity-display">{product.quantity || 1}</span>
                        <BootstrapButton
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleNewProductQuantityChange(product.id, 1)}
                        >
                          <BsPlus />
                        </BootstrapButton>
                      </div>
                      <BootstrapButton
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveNewProduct(product.id)}
                        className="delete-button"
                        title={t('remove')}
                      >
                        <AiOutlineDelete />
                      </BootstrapButton>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          )}
        </Col>
      </Row>
    </ModalCustom>
  );
};

AddItemsToOrderModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  order: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
  }),
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      quantity: PropTypes.number,
      precio_venta: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        cover: PropTypes.string,
        editorial: PropTypes.string,
      }),
    })
  ),
  loading: PropTypes.bool,
};

export default AddItemsToOrderModal;